import { stateMappingFromStageMap, type StateAction, type StateMapping } from "@jump-pipelines/core";

/** Plain-language chrome for one destination object type. BRAND.md: the audience
 *  is CS and onboarding, so every label names the thing an operator can see in
 *  HubSpot — an order, a deal, an item — and never a transform or a directive. */
export interface StateMappingCopy {
  heading: string;
  syncLabel: string;
  archiveLabel: string;
  /** The bare noun for this object, so the caution names the same thing the
   *  chosen option does ("Deletes the item", not "Deletes the item" under a row
   *  labelled "the deal"). */
  noun: string;
  stageColumnLabel: string;
  termKey: "orderStatus" | "itemStatus";
}

const COPY: Record<string, StateMappingCopy> = {
  "hubspot.deal": {
    heading: "WHEN AN ORDER CHANGES STATUS",
    syncLabel: "Update the deal",
    archiveLabel: "Delete the deal in HubSpot",
    noun: "deal",
    stageColumnLabel: "DEAL STAGE",
    termKey: "orderStatus",
  },
  "hubspot.line_item": {
    heading: "WHEN AN ITEM CHANGES STATUS",
    syncLabel: "Update the item",
    // Says what the action does. "Remove it from the deal" is, in HubSpot's own
    // vocabulary, what you do to DISASSOCIATE a line item — the record survives
    // — and this issues a DELETE (packages/connectors-hubspot/src/api-client.ts).
    // An operator reading the old label could pick it believing it was a
    // reversible unlink.
    archiveLabel: "Delete the item in HubSpot",
    noun: "item",
    stageColumnLabel: "STAGE",
    termKey: "itemStatus",
  },
};

const FALLBACK_COPY: StateMappingCopy = {
  heading: "WHEN A RECORD CHANGES STATUS",
  syncLabel: "Update the record",
  archiveLabel: "Delete the record",
  noun: "record",
  stageColumnLabel: "STAGE",
  termKey: "orderStatus",
};

export function copyFor(destObjectType: string | undefined): StateMappingCopy {
  return (destObjectType && COPY[destObjectType]) || FALLBACK_COPY;
}

export const SKIP_LABEL = "Leave it alone";

/** The stage cell's first option. Never blank: a blank cell would read as
 *  "unset" and hide the fact that every other jump_* property still writes. */
export const NO_STAGE_LABEL = "Don't change the stage";

/** Offered in the status field's datalist. Suggestions, never a closed list —
 *  the producer vocabulary is still in flux (spec §4.3). */
export const SUGGESTED_STATES = ["CONFIRMED", "CANCELLED", "FAILED_CHECKOUT"];

/** Shown once, under the table, when any row deletes a record. Factual, not
 *  alarming — the operator's choice stands. Takes `copy` so the noun matches the
 *  row's own option rather than hardcoding "item" under a deal or a fallback.
 *
 *  "Not just its link to the deal" is the distinction that matters for the only
 *  object that can be deleted today: a line item, whose declared `canArchive`
 *  is what puts the option on screen at all (`actionOptions`). */
export const archiveCaution = (copy: StateMappingCopy) =>
  `Deletes the ${copy.noun} in HubSpot — not just its link to the deal. HubSpot keeps it in the recycling bin for 90 days; restoring it is manual.`;

/** Appended to the caution when the pipeline adopts (spec §14). An explicit
 *  delete rule is precisely the thing that lets Anansi delete a record the
 *  customer created — the guard steps aside for it — and this is the only place
 *  the person choosing the rule is told so. */
export const ADOPTION_DELETE_CAUTION =
  "This pipeline writes to records your customer already created. A delete rule will delete those too.";

/** Shown when the pipeline adopts and nothing on the screen deletes anything
 *  (spec §14). The built-in cancellation removal is refused on an adopting
 *  pipeline, so a cancelled item stays in HubSpot — the amendment's
 *  over-refusal, said on the screen where the operator would look for it
 *  instead of only in a log line and a `skipped` run. Takes `copy` so the noun
 *  matches the rows it sits under. */
export const adoptionRefusalNotice = (copy: StateMappingCopy) =>
  `A cancelled ${copy.noun} stays in HubSpot. This pipeline writes to records your customer already created, so Anansi only removes one where a delete rule says to.`;

/** Dry-run banner copy (spec §15.2). Shown while a mapping resolves every
 *  decision but is not yet in effect — the operator can watch it against live
 *  traffic before turning it on.
 *
 *  What dry run promises is "these rules are not in effect", NOT "no HubSpot
 *  writes". The pipeline keeps syncing, and on a line item the built-in
 *  cancellation removal keeps running — so an operator who adds
 *  `CANCELLED → Update the item` to STOP items disappearing, and reads that
 *  nothing is changing in HubSpot, would conclude the opposite of what is
 *  happening. Say what is actually true. */
export const DRY_RUN_BANNER =
  "Dry run — these rules aren't in effect yet. Anansi records what each one would do; the pipeline keeps behaving exactly as it does today.";
export const DRY_RUN_PROMOTE = "Turn these rules on";
export const DRY_RUN_DEMOTE = "Put back in dry run";

/** The actions a row may choose. `archive` needs the destination's permission
 *  AND a named status: the "Anything else" fallback never removes anything, so
 *  an unrecognised status can never delete a record (spec §5). */
export function actionOptions(
  copy: StateMappingCopy,
  opts: { canArchive?: boolean; isFallback?: boolean },
): Array<{ value: StateAction; label: string }> {
  const options: Array<{ value: StateAction; label: string }> = [
    { value: "sync", label: copy.syncLabel },
    { value: "skip", label: SKIP_LABEL },
  ];
  if (opts.canArchive === true && opts.isFallback !== true) {
    options.push({ value: "archive", label: copy.archiveLabel });
  }
  return options;
}

/**
 * The mapping to write the first time an operator enables the feature.
 *
 * Equal to the pipeline's current effective behavior — the operator's legacy
 * stage map, then the transform's built-ins for anything it does not name — so
 * enabling changes nothing until a row is edited (spec Goal 5, §10). With
 * nothing to reproduce, open one blank row so the table is usable immediately.
 */
export function firstEnableMapping(ctx: {
  legacyStageMap?: Record<string, string>;
  defaultStateStages?: Record<string, string>;
}): StateMapping {
  const mapping = stateMappingFromStageMap(ctx.legacyStageMap, ctx.defaultStateStages);
  // Dry-run on first enable (spec §15.2). The pre-fill already reproduces current
  // behavior, so this is belt and braces for the pre-fill — and the only thing
  // standing between a first edit and a live deletion.
  if (mapping.rules.length > 0) return { ...mapping, mode: "dry-run" };
  return blankMapping();
}

/**
 * One empty row, dry-run. What `firstEnableMapping` writes when there is no
 * legacy behavior to reproduce, and what the editor falls back to when the
 * pre-fill refuses to build (a legacy map with more distinct states than
 * `MAX_STATE_RULES`). Shared so the two cannot drift into writing different
 * starting shapes.
 */
export function blankMapping(): StateMapping {
  return { version: 1, mode: "dry-run", rules: [{ state: "", action: "sync" }], unmapped: "sync" };
}
